import { useRef, useCallback, useState } from "react"
import jsQR from 'jsqr'
export const useScanner = (options = {}) => {
  const {
    fps = 8,
    workerPath = './scanWorker.js', // 新增: 默认的worker文件
    autoScan = true, // 新增: 是否自动扫描
    onResult,
    onError = () => {},
  } = options

  const lastSent = useRef(0)
  const workerRef = useRef(null)
  const videoRef = useRef(null)
  const rAFId = useRef(null)
  const [imageDetail, setImageDetail] = useState(null)
  
  // 直接在主线程扫描
  const scanFrame = useCallback((bitmap) => {
    if (bitmap) {
      const imageData = bit2data(bitmap)
      const result = jsQR(imageData.data, imageData.width, imageData.height);
      console.log(result, 'jsQR返回的结果')
      // 有识别结果才返回
      if (result) {
        const data = {
          type: 'result',
          result,
          imageData
        }
        setImageDetail(imageData)
        onResult(data)
        return data
      } else {
        onError()
      }
    }
  }, [onResult, onError])

  // 发送帧
  const sendFrame = useCallback(async () => {
    const video = videoRef.current
    if (!video || video.readyState < 2) return
    const now = performance.now()
    const minGap = 1000 / fps
    if (now - lastSent.current < minGap && autoScan) return
    lastSent.current = now
    try {
      const bitmap = await createImageBitmap(video)
      console.log(bitmap, 'bitmap')
      // 如果是自动扫描
      if (autoScan) {
        workerRef.current.postMessage(
          { type: 'frame', bitmap },
        )
      // 如果不是, 则直接在本地进行
      } else {
        return scanFrame(bitmap)
      }
    } catch (e) {
      console.error("sendFrame error:", e)
    }
  }, [fps, autoScan, scanFrame])
  // bitmap 转 image data
  function bit2data (bitmap) {
    let offscreen, ctx
    // 直接使用ImageData进行QR码识别
    if (bitmap) {
      offscreen = new OffscreenCanvas(bitmap.width, bitmap.height)
      ctx = offscreen.getContext("2d", { willReadFrequently: true })
      ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height)
      const imgData = ctx.getImageData(0, 0, bitmap.width, bitmap.height)
      return imgData
    }
  }
  // 开始帧循环
  const startPump = useCallback(() => {
    const video = videoRef.current
    const hasRVFC = "requestVideoFrameCallback" in video
    const loop = () => {
      sendFrame()
      if (hasRVFC) {
        rAFId.current = video.requestVideoFrameCallback(loop)
      } else {
        rAFId.current = requestAnimationFrame(loop)
      }
    }
    if (hasRVFC) {
      rAFId.current = video.requestVideoFrameCallback(loop)
    } else {
      rAFId.current = requestAnimationFrame(loop)
    }
  }, [sendFrame])

  // 截图
  const captureImg = async () => {
    const video = videoRef.current
    
    // 创建临时 canvas 来获取 base64
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    // 设置 canvas 尺寸为视频尺寸
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    // 将视频帧绘制到 canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    // 获取 base64 数据
    const base64 = canvas.toDataURL('image/jpeg', 0.8)
    
    return base64
  }
  // 启动
  const start = useCallback(async () => {
    if (autoScan) {
      // 1. 新建 worker (支持传参)
      const worker = workerRef.current = new Worker(
        new URL('./scanWorker.js', import.meta.url),
        { type: 'module' }
      )
  
      // 是否可以使用 offscreenCanvas
      const useOffscreenCanvas = typeof OffscreenCanvas !== 'undefined'
      worker.postMessage({ type: 'init', useOffscreenCanvas })
  
      worker.onmessage = (e) => {
        // 统一交给外部处理更好（可扩展）
        if (options.onResult) {
          onResult(e.data)
        } else {
          console.log("Worker message:", e.data)
        }
      }
    }

    try {
      // 2. 获取摄像头
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      })
      videoRef.current.srcObject = stream
    } catch(error) {
      console.log('Error accessing camera', error)
    }

    if (autoScan) {
      // 3. 启动循环
      startPump()
    }
  }, [startPump, autoScan, options, onResult])

  // 停止扫描并清理资源
  const stop = useCallback(() => {
    // 停止帧循环
    if (rAFId.current) {
      if (videoRef.current && 'cancelVideoFrameCallback' in videoRef.current) {
        videoRef.current.cancelVideoFrameCallback(rAFId.current)
      } else {
        cancelAnimationFrame(rAFId.current)
      }
      rAFId.current = null
    }

    // 停止摄像头流
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject
      stream.getTracks().forEach(track => track.stop())
      videoRef.current.srcObject = null
    }

    // 终止 worker
    if (workerRef.current) {
      workerRef.current.terminate()
      workerRef.current = null
    }
  }, [])

  return {
    videoRef,
    start,
    stop,
    sendFrame,
    captureImg,
    imageDetail
  }
}

